import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import DirectionsRunRoundedIcon from '@mui/icons-material/DirectionsRunRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import BatteryChargingFullRoundedIcon from '@mui/icons-material/BatteryChargingFullRounded';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { reportsService } from '../../services/reports.service';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { StatCard } from '../../components/common/StatCard';
import { LoadingSkeleton } from '../../components/common/LoadingSkeleton';
import { PageHeader } from '../../components/common/PageHeader';
import { EmptyState } from '../../components/common/EmptyState';
import { tokens } from '../../theme';
import type { Employee } from '../../types';

type EmployeeStatusType = 'active' | 'idle' | 'offline';

function getEmployeeStatusInfo(employee: Employee): {
  status: EmployeeStatusType;
  label: string;
  color: string;
  bg: string;
} {
  if (employee.lastLocation) {
    const minutesAgo = (Date.now() - new Date(employee.lastLocation.timestamp).getTime()) / 60000;
    if (minutesAgo < 5) {
      return { status: 'active', label: 'Active', color: tokens.status.success.text, bg: tokens.status.success.bg };
    }
    if (minutesAgo < 30) {
      return { status: 'idle', label: 'Idle', color: tokens.status.warning.text, bg: tokens.status.warning.bg };
    }
    return { status: 'offline', label: 'Interrupted', color: tokens.status.danger.text, bg: tokens.status.danger.bg };
  }
  if (employee.onDuty) {
    return { status: 'active', label: 'Active', color: tokens.status.success.text, bg: tokens.status.success.bg };
  }
  return { status: 'offline', label: 'Off Duty', color: tokens.status.neutral.text, bg: tokens.status.neutral.bg };
}

export function ManagerDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    data: stats,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['manager-dashboard-stats'],
    queryFn: () => reportsService.getManagerDashboardStats(),
    refetchInterval: 10000,
  });

  if (error) return <ErrorMessage onRetry={refetch} />;
  if (isLoading && !stats) return <LoadingSkeleton variant="page" cardCount={4} />;

  return (
    <Box>
      <PageHeader
        compact
        title="Team Dashboard"
        subtitle="Monitor your field team's activity and visit progress"
      />

      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="My Employees"
            value={stats?.myEmployees ?? 0}
            icon={<GroupRoundedIcon />}
            tone="primary"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Active Today"
            value={stats?.activeToday ?? 0}
            icon={<DirectionsRunRoundedIcon />}
            tone="success"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Visits Done"
            value={stats?.visitsCompleted ?? 0}
            icon={<CheckCircleRoundedIcon />}
            tone="info"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Pending Visits"
            value={stats?.pendingVisits ?? 0}
            icon={<PendingActionsRoundedIcon />}
            tone="warning"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={700} mb={0.5} letterSpacing="-0.01em">
            Team Activity
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Live status based on recent GPS updates
          </Typography>

          {isLoading ? (
            <LoadingSkeleton variant="table" />
          ) : stats?.teamActivity?.length ? (
            <List disablePadding>
              {stats.teamActivity.map((item, i) => {
                const statusInfo = getEmployeeStatusInfo(item.employee);
                return (
                  <Box key={item.employee.id}>
                    <ListItem
                      disablePadding
                      sx={{
                        py: 1.5,
                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                        gap: isMobile ? 1 : 0,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
                          }}
                        >
                          {item.employee.name.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {item.employee.name}
                            </Typography>
                            <FiberManualRecordRoundedIcon
                              sx={{ fontSize: 8, color: statusInfo.color }}
                              aria-hidden
                            />
                            <Typography
                              component="span"
                              sx={{
                                position: 'absolute',
                                width: 1,
                                height: 1,
                                padding: 0,
                                margin: -1,
                                overflow: 'hidden',
                                clip: 'rect(0,0,0,0)',
                                whiteSpace: 'nowrap',
                                border: 0,
                              }}
                            >
                              Status: {statusInfo.label}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              {item.employee.lastLocation
                                ? `Last seen ${formatDistanceToNow(new Date(item.employee.lastLocation.timestamp), { addSuffix: true })}`
                                : 'No location data'}
                            </Typography>
                            {item.employee.lastLocation?.batteryLevel !== undefined && (
                              <>
                                <Typography variant="caption" color="text.secondary">·</Typography>
                                <BatteryChargingFullRoundedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {item.employee.lastLocation.batteryLevel}%
                                </Typography>
                              </>
                            )}
                          </Stack>
                        }
                      />
                      <ListItemSecondaryAction
                        sx={{
                          position: isMobile ? 'static' : 'absolute',
                          transform: isMobile ? 'none' : undefined,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          width: isMobile ? '100%' : 'auto',
                          justifyContent: isMobile ? 'flex-start' : 'flex-end',
                          pl: isMobile ? 7 : 0,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {item.visitsToday} visits
                        </Typography>
                        <Chip
                          label={statusInfo.label}
                          size="small"
                          sx={{
                            bgcolor: statusInfo.bg,
                            color: statusInfo.color,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    {i < stats.teamActivity.length - 1 && <Divider sx={{ ml: { xs: 0, sm: 7 } }} />}
                  </Box>
                );
              })}
            </List>
          ) : (
            <EmptyState
              title="No team activity yet"
              description="When your field employees start their workday, their status will appear here."
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
